// Pure Chargebee subscription.status classification — no React, no module
// aliases — so the React hooks (subscriptionAccess.ts), the nag banner, the
// lockout gate, and unit tests can all share one definition of what each
// status means. If these sets drift, paying owners get locked out or ended
// subscriptions keep access, so they're deliberately kept in one tiny file.

// Installers that bundle the Powston subscription into their own offering.
// Their customers are billed via the installer, not Powston directly, so
// they're never nagged and never locked out.
export const SUBSCRIPTION_EXEMPT_INSTALLER_IDS = new Set<number>([37, 50]);

export type SubscriptionSite = {
  owner_user_id?: number | null;
  owner_email?: string | null;
  installer_id?: number | null;
};

export type SubscriptionOwnership = {
  ownsSite: boolean;
  exemptByInstaller: boolean;
};

// Legacy duplicate-user records can authenticate as one user id while the
// installer-created site belongs to another row with the same email. Treat
// either identity match as ownership, but do not let unrelated shared sites
// grant an installer exemption to the whole account.
export function classifySubscriptionSites(
  userId: number,
  userEmail: string | null | undefined,
  sites: SubscriptionSite[],
): SubscriptionOwnership {
  const email = (userEmail ?? '').trim().toLowerCase();
  const ownedSites = sites.filter(site => {
    if (site?.owner_user_id === userId) return true;
    const ownerEmail = (site?.owner_email ?? '').trim().toLowerCase();
    return email !== '' && ownerEmail === email;
  });

  return {
    ownsSite: ownedSites.length > 0,
    exemptByInstaller: ownedSites.some(
      site => typeof site?.installer_id === 'number'
        && SUBSCRIPTION_EXEMPT_INSTALLER_IDS.has(site.installer_id),
    ),
  };
}

// Statuses that mean the subscription has ended. An owner in one of these
// states (on a non-exempt site) has lost access.
const TERMINATED_STATUSES = new Set(['cancelled', 'transferred']);

// Statuses that count as a live or pending-live subscription — nothing to
// nag about or block.
const ACTIVE_LIKE_STATUSES = new Set([
  'active', 'in_trial', 'non_renewing', 'paused', 'future',
]);

export function isActiveLikeStatus(status?: string | null): boolean {
  return ACTIVE_LIKE_STATUSES.has((status ?? '').toLowerCase());
}

export function isTerminatedStatus(status?: string | null): boolean {
  return TERMINATED_STATUSES.has((status ?? '').toLowerCase());
}
